/**
 * Mobile App Component
 * Main container for mobile experience with bottom navigation
 * Handles authentication and routing
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { leaguesAPI } from '../../services/apiService';
import { mobilePageBackground } from '../../services/mobileStyleService';
import MobileAuthFlow from './auth/MobileAuthFlow';
import BottomNav from './BottomNav';
import MobileTeamHome from './pages/MobileTeamHome';
import MobileRoster from './pages/MobileRoster';
import MobilePlayerSearch from './pages/MobilePlayerSearch';
import MobileHome from './pages/MobileHome';
import MobileLeagueHome from './pages/MobileLeagueHome';
import MobileLeagues from './pages/MobileLeagues';
import MobileTeams from './pages/MobileTeams';
import MobileNews from './pages/MobileNews';
import MobileStats from './pages/MobileStats';
import MobileAccount from './pages/MobileAccount';
import MobileWatchList from './pages/MobileWatchList';
import MobileProbables from './pages/MobileProbables';
import JoinLeague from '../../pages/JoinLeague';
import { accountAPI } from '../../services/apiService';

function MobileApp() {
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('home'); // Default to home
  const serverDefaultLeague = useRef(null);
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const cachedLeagues = useRef([]);

  // Cache leagues + fetch server-side default league for bottom nav routing
  useEffect(() => {
    if (isAuthenticated) {
      leaguesAPI.getMyLeagues()
        .then(res => {
          if (res?.success && res.leagues) cachedLeagues.current = res.leagues;
        })
        .catch(() => {});
      accountAPI.getDefaultLeague()
        .then(id => { serverDefaultLeague.current = id; })
        .catch(() => {});
    }
  }, [isAuthenticated]);
  
  // Extract leagueId from URL path (e.g., /leagues/abc-123)
  const extractLeagueIdFromPath = () => {
    const path = location.pathname;
    if (path.startsWith('/leagues/')) {
      const parts = path.split('/');
      return parts[2] || null; // /leagues/[leagueId]
    }
    return params.leagueId || null;
  };

  const leagueId = extractLeagueIdFromPath();
  const isLeagueRoute = location.pathname.startsWith('/leagues/');

  // Only set initial tab based on URL on mount - don't interfere with user clicks
  useEffect(() => {
    const path = location.pathname;
    
    // Don't interfere with join-league route
    if (path === '/join-league') {
      return;
    }
    
    // Set initial tab based on URL, but only if we're on dashboard
    if (path === '/dashboard' || path === '/') {
      setActiveTab('home');
    }
    
    // CRITICAL: On initial load, if authenticated and on league route, redirect to home
    if (isAuthenticated && path.startsWith('/leagues/')) {
      console.log('🏠 Redirecting from league route to home on app load');
      navigate('/dashboard', { replace: true });
      setActiveTab('home');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // Monitor location changes - DON'T override user's tab selection
  useEffect(() => {
    const path = location.pathname;
    
    // Don't interfere with join-league route
    if (path === '/join-league') {
      return;
    }
    
    // Only update tab if explicitly on home routes
    if (path === '/dashboard' || path === '/') {
      // Don't override if user just changed tab - only set on initial load
      // Check if this is from navigation or user clicking
      if (!activeTab || activeTab === 'home') {
        setActiveTab('home');
      }
    }
  }, [location.pathname]); // Removed activeTab from deps to prevent override

  // Handle successful authentication
  const handleAuthSuccess = async () => {
    // Wait a moment for the cookie to be set by the browser
    // Then verify auth status before navigating
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Verify authentication status before navigating
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://13cpdms4x1.execute-api.us-east-1.amazonaws.com/Prod'}/api/auth/status`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.authenticated) {
        // Force navigate to home screen, clearing any old league URLs
        navigate('/dashboard', { replace: true });
        setActiveTab('home');
      } else {
        // Auth verification failed, stay on auth screen
        console.warn('Auth verification failed after login');
      }
    } catch (err) {
      console.error('Error verifying auth status:', err);
      // Still navigate - the cookie might be set even if verification fails
      navigate('/dashboard', { replace: true });
      setActiveTab('home');
    }
  };

  // Resolve default league at tap time
  const getDefaultLeagueId = useCallback(() => {
    const leagues = cachedLeagues.current;
    if (!leagues || leagues.length === 0) return null;
    if (leagues.length === 1) return leagues[0].league_id;
    const stored = serverDefaultLeague.current;
    if (stored && leagues.some(l => l.league_id === stored)) return stored;
    return null; // "Always ask" or no default set
  }, []);

  // If on join-league route, always show JoinLeague component (it handles its own auth flow)
  if (location.pathname === '/join-league') {
    return <JoinLeague />;
  }

  // If not authenticated, show auth flow (after all hooks)
  if (!isAuthenticated) {
    return <MobileAuthFlow onAuthSuccess={handleAuthSuccess} />;
  }

  // Render the appropriate page based on active tab OR route
  const renderPage = () => {
    // Handle invitation/join league route - must be checked first
    if (location.pathname === '/join-league') {
      return <JoinLeague />;
    }

    // Handle league-specific routes
    if (isLeagueRoute && leagueId) {
      // Check if it's a team-specific route
      if (location.pathname === `/leagues/${leagueId}/team`) {
        return <MobileTeamHome leagueId={leagueId} />;
      }
      // Default: show league home
      return <MobileLeagueHome leagueId={leagueId} />;
    }

    // Handle tab-based navigation
    switch (activeTab) {
      case 'home':
        return <MobileHome />;
      case 'leagues':
        return <MobileLeagues />;
      case 'teams':
        return <MobileTeams />;
      case 'news':
        // 9n: onNavigate enables "View All Probables →" link in GamesTabContent
        // to switch activeTab to 'probables'.
        return <MobileNews onNavigate={setActiveTab} />;
      case 'stats':
        return <MobileStats />;
      case 'account':
        return <MobileAccount onNavigate={setActiveTab} />;

      case 'watchlist':
        // 9l.2: mobile watch list — accessed from Account "Watch List" menu item.
        return <MobileWatchList />;

      case 'probables':
        // 9n: dedicated mobile probable-pitchers page — accessed from News tab's
        // "Upcoming Probables" section via "View All Probables →" link.
        return <MobileProbables onNavigate={setActiveTab} />;

      // Legacy support for team-specific pages (when navigating from leagues/teams)
      case 'roster':
        return leagueId ? <MobileRoster leagueId={leagueId} /> : <MobileLeagues />;
      case 'players':
        return leagueId ? <MobilePlayerSearch leagueId={leagueId} /> : <MobileLeagues />;
      
      default:
        return <MobileLeagues />;
    }
  };

  // Handle tab changes - navigate away from league routes
  const handleTabChange = (tabId) => {
    // Active state must update on EVERY tap — set it first, before any branch or
    // early return below (the leagues/teams branch returns early after navigate).
    setActiveTab(tabId);

    // For Leagues/Teams: route to default league if one is resolved
    if (tabId === 'leagues' || tabId === 'teams') {
      const defaultId = getDefaultLeagueId();
      if (defaultId) {
        if (tabId === 'teams') {
          navigate(`/leagues/${defaultId}/team`, { replace: true });
        } else {
          navigate(`/leagues/${defaultId}`, { replace: true });
        }
        return;
      }
      // No default — fall through to selection page
    }

    // Only navigate if:
    // 1. Going to 'home' - always navigate to dashboard
    // 2. Currently on a league route - navigate to dashboard so tab content can render
    if (tabId === 'home') {
      navigate('/dashboard', { replace: true });
    } else if (isLeagueRoute) {
      // Navigate away from league route so the tab page can render
      navigate('/dashboard', { replace: true });
    }
    // Otherwise, just change tab state and let renderPage() handle it
  };

  // Don't show bottom nav on join-league route
  const showBottomNav = location.pathname !== '/join-league';

  return (
    <div style={{
      background: mobilePageBackground,
      overflowX: 'clip',
      minHeight: '100vh',
    }}>
      <div style={{ paddingBottom: showBottomNav ? '70px' : '0' }}>
        {renderPage()}
      </div>
      {showBottomNav && <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />}
    </div>
  );
}

export default MobileApp;
